import classNames from "classnames";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import styles from "./AscFilesUploader.module.scss";
import {
  CircleCloseIcon,
  EyeIcon,
  FileIcon,
  UploadOutlineIcon,
} from "../AscIcons";
import AscButton from "../AscButton/AscButton";
import AscProgressBar from "../AscProgressBar/AscProgressBar";
import { AscFile, normalizeFileSize } from "@ascendnow/shared";
import AscFileViewModal from "../AscFileViewModal/AscFileViewModal";
import AscVideoPlayer, {
  VideoPlayerCaption,
} from "../AscVideoPlayer/AscVideoPlayer";

type UseDropZoneProps = Required<Parameters<typeof useDropzone>>[0];

interface AscFilesUploaderProps {
  className?: string;
  classNameFileList?: string;
  icon?: React.ReactNode;
  withBr?: boolean;
  multiple?: boolean;
  iconWrapperClassName?: string;
  textClassName?: string;
  accept?: UseDropZoneProps["accept"];
  onDrop?: UseDropZoneProps["onDrop"];
  onRemoveFile?: () => void;
  srcFile?: string;
  srcText?: string;
  fileClassName?: string;
  closeIcon?: boolean;
  dropZoneProps?: Omit<UseDropZoneProps, "accept" | "onDrop">;
  id?: string;
  name?: string;
  initialValue?: File[];
  nameFile?: string;
  maxFiles?: number;
  type?: "image" | "video" | "file";
  onChange?: (files: File[]) => void;
  videoClassName?: string;
  /**
   * This is used to set the value of the field in the formik form.
   */
  setFieldValue?: (name: string, files: File[] | null) => void;
  readOnlyMode?: boolean;
  readOnlyFiles?: AscFile[];
  disabled?: boolean;
  compact?: boolean;
  /**
   * Number ranging from 0 to 100
   */
  uploadProgress?: number[];
  removeFile?: (index: number) => void;
  srcFileClassName?: string;
  videoCaptions?: VideoPlayerCaption[];
}

export interface AscFilesUploaderRef {
  handleRemoveFile: (index: number) => void;
}

const AscFilesUploader = forwardRef<AscFilesUploaderRef, AscFilesUploaderProps>(
  (
    {
      className,
      classNameFileList,
      iconWrapperClassName,
      accept,
      initialValue,
      onDrop,
      onRemoveFile,
      srcFile,
      srcText,
      fileClassName,
      type = "image",
      nameFile = "",
      closeIcon = true,
      dropZoneProps,
      multiple = true,
      id,
      name,
      onChange,
      setFieldValue,
      disabled,
      uploadProgress,
      readOnlyFiles,
      readOnlyMode,
      compact = false,
      maxFiles,
      srcFileClassName,
      videoCaptions,
      videoClassName,
    },
    ref
  ) => {
    const [files, setFiles] = useState<File[]>([]);
    const [fileIdToShow, setFileIdToShow] = useState<string>();
    const { getRootProps, getInputProps } = useDropzone({
      accept,
      onDrop: handleOnDrop,
      disabled: disabled || readOnlyMode || maxFiles === files?.length,
      multiple,
      ...dropZoneProps,
    });

    function handleOnDrop(
      ...args: Parameters<Required<UseDropZoneProps>["onDrop"]>
    ) {
      const acceptedFiles = args?.[0];
      setFiles((prev) => {
        const files = prev?.concat(acceptedFiles)?.slice(0, maxFiles);
        onChange?.(files);
        return files;
      });
      onDrop?.(...args);
    }

    function handleRemoveFile(index: number) {
      setFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles.splice(index, 1);
        onChange?.(newFiles);
        return newFiles;
      });
    }

    useImperativeHandle(ref, () => ({
      handleRemoveFile,
    }));

    function handleRemoveSingleFile() {
      if (onRemoveFile) {
        onRemoveFile();
      }
      if (setFieldValue && name) {
        setFieldValue(name, null);
        onChange?.([]);
      }
      setFiles([]);
      onChange?.([]);
    }

    useEffect(() => {
      if (!name) {
        return;
      }
      setFieldValue?.(name, files);
    }, [files, name, setFieldValue]);

    useEffect(() => {
      if (!initialValue || disabled) {
        return;
      }

      setFiles(initialValue?.slice(0, maxFiles));
      onChange?.(initialValue?.slice(0, maxFiles));
    }, [disabled]);

    return (
      <div
        className={classNames(
          styles.ground,
          {
            [styles.disabled]:
              disabled || readOnlyMode || maxFiles === files?.length,
            [styles.compact]: compact,
          },
          className
        )}
      >
        {srcFile && srcFile.length > 0 ? (
          <div className={classNames(styles.fileWrapper, fileClassName)}>
            {type === "image" && (
              <img
                src={srcFile}
                alt={srcFile}
                className={classNames(styles.image, srcFileClassName)}
              />
            )}
            {type === "file" && (
              <div className={styles.filesList}>
                <div className={styles.fileItem}>
                  <FileIcon className={styles.fileIcon} />
                  <div className={styles.fileTextContent}>
                    <div className={styles.fileName}>{nameFile}</div>
                  </div>
                </div>
              </div>
            )}
            {type === "video" && (
              <AscVideoPlayer
                url={srcFile}
                captions={videoCaptions}
                className={videoClassName}
              />
            )}
            {closeIcon && (
              <AscButton
                className={styles.removeSingleButton}
                onClick={handleRemoveSingleFile}
                variant="white"
                icon={<CircleCloseIcon className={styles.removeButtonIcon} />}
              />
            )}
          </div>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {(multiple || files.length < 1) && !readOnlyMode && (
              <div
                {...getRootProps()}
                className={classNames(styles.dropZone, classNameFileList)}
              >
                <input
                  {...getInputProps({
                    id,
                    name,
                    disabled,
                  })}
                />

                <div
                  className={classNames(
                    styles.uploadIconWrapper,
                    iconWrapperClassName
                  )}
                >
                  <UploadOutlineIcon className={styles.uploadIcon} />
                </div>

                <div className={classNames(styles.text)}>
                  {srcText ? (
                    srcText
                  ) : (
                    <>
                      Drop files here to upload or <u>choose file</u>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {typeof srcFile !== "string" && (
          <ul
            className={classNames(styles.filesList, {
              [classNameFileList ? classNameFileList : ""]: files.length > 0,
            })}
          >
            {readOnlyMode
              ? readOnlyFiles?.map((file, index) => (
                  <li className={styles.fileItem} key={index}>
                    <FileIcon className={styles.fileIcon} />
                    <div className={styles.fileTextContent}>
                      <div className={styles.fileName}>{file?.fileName}</div>

                      <div className={styles?.fileSize}>
                        {normalizeFileSize(file?.fileSize || 0)}
                      </div>
                    </div>
                    <AscButton
                      onClick={() => setFileIdToShow(file?.id)}
                      variant="white"
                      icon={<EyeIcon />}
                    />
                    <AscFileViewModal
                      visible={fileIdToShow === file?.id}
                      fileName={file?.fileName}
                      fileUrl={file?.fileUrl ?? file?.location}
                      fileType={file?.fileType || "other"}
                      onClose={() => setFileIdToShow(undefined)}
                      videoCaptions={
                        file?.captions && file?.captions?.length > 0
                          ? file?.captions?.[0]?.translations
                          : typeof file?.captions === "object"
                          ? file?.captions?.translations
                          : []
                      }
                    />
                  </li>
                ))
              : files?.map((file, index) => (
                  <li className={styles.fileItem} key={index}>
                    <FileIcon className={styles.fileIcon} />
                    <div className={styles.fileTextContent}>
                      <div className={styles.fileName}>{file?.name}</div>
                      {uploadProgress?.[index] ? (
                        <AscProgressBar
                          className={styles.progressBar}
                          fullWidth
                          value={uploadProgress?.[index]}
                          max={100}
                        />
                      ) : (
                        <div className={styles?.fileSize}>
                          {normalizeFileSize(file?.size || 0)}
                        </div>
                      )}
                    </div>
                    <AscButton
                      className={styles.removeButton}
                      onClick={() => handleRemoveFile(index)}
                      variant="white"
                      icon={
                        <CircleCloseIcon className={styles.removeButtonIcon} />
                      }
                    />
                  </li>
                ))}
          </ul>
        )}
      </div>
    );
  }
);
export default AscFilesUploader;
